import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { Api } from '../../../services/api/api.service';
import { RecentClaimsService } from '../../../services/recent-claims/recent-claims.service';
import { ViewService } from '../../../services/view/view.service';

/**
 * Ensures the correct claim is loaded before ClaimLayoutComponent activates.
 * Handles browser back/forward and page refresh scenarios.
 */
@Injectable({ providedIn: 'root' })
export class ClaimResolver implements Resolve<ClaimRow> {
  private api = inject(Api);
  private view = inject(ViewService);
  private recentClaims = inject(RecentClaimsService);

  async resolve(route: ActivatedRouteSnapshot): Promise<ClaimRow> {
    const recordSource = route.params['recordSource'];
    const recordId = route.params['recordId'];

    if (!recordSource || !recordId) {
      return null;
    }

    // Check if current claim matches route params
    const currentClaim = this.view.currentClaim();
    if (
      currentClaim &&
      currentClaim.recordSource === recordSource &&
      currentClaim.recordId === recordId
    ) {
      // Old sessionStorage may not have ssnMask — refetch so UnMask is current
      if (currentClaim.ssnMask === undefined) {
        const claimHeader = await this.api.claimHeader.get({
          recordSource,
          recordId,
        });
        this.view.setCurrentClaim(claimHeader);
        this.recentClaims.add(claimHeader);
        return claimHeader;
      }
      this.view.applyClaimSsnMasking(currentClaim);
      this.recentClaims.add(currentClaim);
      return currentClaim;
    }

    // Mismatch or no claim - fetch correct claim
    const claimHeader = await this.api.claimHeader.get({
      recordSource,
      recordId,
    });
    this.view.setCurrentClaim(claimHeader);
    this.recentClaims.add(claimHeader);

    return claimHeader;
  }
}
